import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import FormInput from '../components/Form/FormInput';
import FormTextarea from '../components/Form/FormTextarea';
import FormButtons from '../components/Form/FormButtons';

const DAILY_REPORT_API = 'https://system.sales-management.cloud/api/daily_report.php';
const FIELD_DEFINITIONS_API = 'https://system.sales-management.cloud/api/report-field-definitions.php';
const UPLOAD_IMAGE_API = 'https://system.sales-management.cloud/api/upload_image.php';

function Report() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    lateReason: '',
    memo: ''
  });
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [dynamicFormData, setDynamicFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLate, setIsLate] = useState(false);
  const [existingReport, setExistingReport] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [images, setImages] = useState([]);
  const [uploadProgress] = useState(0);
  const [isUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const isApproved = existingReport?.approval_status === 'approved';
  const isReadOnly = existingReport && (!isEditing || isApproved);

  // フィールド定義を取得
  useEffect(() => {
    const fetchFieldDefinitions = async () => {
      try {
        const token = Cookies.get('auth_token');
        if (!token) return;

        const response = await fetch(FIELD_DEFINITIONS_API, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Field definitions API response:', data);
          if (data.status === 'success' && data.data && data.data.fields) {
            console.log('Setting field definitions:', data.data.fields);
            setFieldDefinitions(data.data.fields);
            // 各フィールドの初期値を設定
            const initialFormData = {};
            data.data.fields.forEach((field) => {
              initialFormData[field.field_name] = '';
            });
            console.log('Setting dynamic form data:', initialFormData);
            setDynamicFormData(initialFormData);
          }
        } else {
          console.warn('API response not ok:', response.status);
        }
      } catch (err) {
        console.error('Failed to fetch field definitions:', err);
      }
    };

    fetchFieldDefinitions();
  }, []);

  // 日報一覧を取得
  useEffect(() => {
    const fetchReports = async () => {
      setListLoading(true);
      try {
        const token = Cookies.get('auth_token');
        if (!token) return;

        // 過去30日分の日報を取得
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const response = await fetch(`${DAILY_REPORT_API}?start_date=${startDate}&end_date=${endDate}&limit=30&scope=department`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.status === 'success' && data.data && data.data.reports) {
          setReports(data.data.reports);
        }
      } catch (err) {
        console.error('Failed to fetch reports:', err);
      } finally {
        setListLoading(false);
      }
    };

    fetchReports();
  }, [success]); // success が変わったら再取得

  // 提出が遅延しているかチェック
  useEffect(() => {
    const checkIfLate = () => {
      const now = new Date();
      const workDate = new Date(formData.date);

      // 作業日の23時を計算
      const deadline = new Date(workDate);
      deadline.setHours(23, 0, 0, 0);

      // 現在時刻が期限を過ぎているかチェック
      setIsLate(now > deadline);
    };

    checkIfLate();
  }, [formData.date]);

  // 既存の日報を取得
  useEffect(() => {
    const fetchExistingReport = async () => {
      try {
        const token = Cookies.get('auth_token');

        if (!token) return;
        
        // fieldDefinitions が読み込まれるまで待つ
        if (fieldDefinitions.length === 0) return;

        const response = await fetch(`${DAILY_REPORT_API}?work_date=${formData.date}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.status === 'success' && data.data && data.data.reports && data.data.reports.length > 0) {
          const report = data.data.reports[0];
          console.log('Existing report loaded:', report);
          setExistingReport(report);
          setIsEditing(false);
          // APIレスポンスの構造に対応: daily_report_content.memo を取得
          const memoValue = report.daily_report_content?.memo || report.memo || '';
          // フォームに既存データを設定
          setFormData({
            date: report.work_date || formData.date,
            lateReason: report.late_reason || '',
            memo: memoValue
          });
          // dynamicFormData に daily_report_content を設定
          if (report.daily_report_content && typeof report.daily_report_content === 'object') {
            // ネストされた構造に対応: daily_report_content.daily_report_content を取得
            const actualContent = report.daily_report_content.daily_report_content || report.daily_report_content;
            console.log('Setting dynamic form data from existing report:', actualContent);
            setDynamicFormData(actualContent);
          } else {
            const initialData = {};
            fieldDefinitions.forEach(field => {
              initialData[field.field_name] = '';
            });
            console.log('Initializing empty dynamic form data:', initialData);
            setDynamicFormData(initialData);
          }
        } else {
          setExistingReport(null);
          setIsEditing(true);
          // 既存の日報がない場合、空のフィールドで初期化
          const initialData = {};
          fieldDefinitions.forEach(field => {
            initialData[field.field_name] = '';
          });
          setDynamicFormData(initialData);
        }
      } catch (err) {
        console.error('Failed to fetch existing report:', err);
        setExistingReport(null);
        setIsEditing(true);
      }
    };

    fetchExistingReport();
  }, [formData.date, fieldDefinitions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    // 画像ファイルのみフィルタ
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      setError('画像ファイルのみアップロードできます');
      return;
    }

    // 最大50枚まで制限
    if (images.length + imageFiles.length > 50) {
      setError('画像は最大50枚までアップロードできます');
      return;
    }

    // プレビュー情報を含めて画像を追加
    const newImages = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      imageId: null
    }));

    setImages(prev => [...prev, ...newImages]);
    setError('');

    // Cookie からトークンを取得
    const token = Cookies.get('auth_token');
    if (!token) {
      setError('認証情報が見つかりません。再度ログインしてください。');
      return;
    }

    // 即時アップロード開始
    const startIndex = images.length;
    for (let i = 0; i < newImages.length; i++) {
      try {
        await uploadSingleImage(newImages[i], startIndex + i, token);
      } catch (error) {
        console.error(`画像 ${i + 1} のアップロードに失敗:`, error);
      }
    }
  };

  const handleRemoveImage = (index) => {
    setImages(prev => {
      // プレビューURLを解放
      if (prev[index]?.preview) {
        URL.revokeObjectURL(prev[index].preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // 個別画像アップロード
  const uploadSingleImage = async (imageObj, index, token) => {
    const formData = new FormData();
    formData.append('image', imageObj.file);
    formData.append('type', 'daily_report');

    try {
      const response = await fetch(UPLOAD_IMAGE_API, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('アップロード失敗');
      }

      const result = await response.json();

      // ステータス更新 (temp_file_idを使用)
      const tempFileId = result.data.temp_file_id || result.data.image_id || result.data.file_id;
      setImages(prev => prev.map((img, i) =>
        i === index ? { ...img, status: 'uploaded', imageId: tempFileId } : img
      ));

      return tempFileId;
    } catch (error) {
      // エラーステータス更新
      setImages(prev => prev.map((img, i) =>
        i === index ? { ...img, status: 'error' } : img
      ));
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (existingReport && existingReport.approval_status === 'approved') {
        setError('承認済みの日報は編集できません。');
        setLoading(false);
        return;
      }
      // Cookieからトークンを取得
      const token = Cookies.get('auth_token');

      if (!token) {
        setError('認証情報が見つかりません。再度ログインしてください。');
        return;
      }

      // 1. 画像をアップロード
      let imageIds = [];
      if (images.length > 0) {
        // アップロード済みの画像IDを収集
        imageIds = images
          .filter(img => img.status === 'uploaded' && img.imageId)
          .map(img => img.imageId);

        // アップロード失敗した画像があるかチェック
        const hasErrors = images.some(img => img.status === 'error');
        const hasPending = images.some(img => img.status === 'pending');

        if (hasErrors) {
          setError('一部の画像のアップロードに失敗しています。画像を削除するか、再度選択してください。');
          setLoading(false);
          return;
        }

        if (hasPending) {
          setError('画像のアップロード中です。しばらくお待ちください。');
          setLoading(false);
          return;
        }
      }

      // 2. 日報を送信（JSON形式）
      const requestBody = {
        work_date: formData.date,
        ...dynamicFormData  // ユーザーが定義したフィールドをすべて含める
      };

      // オプショナルフィールドを追加
      if (formData.memo) requestBody.memo = formData.memo;
      if (formData.lateReason) requestBody.late_reason = formData.lateReason;
      if (imageIds.length > 0) requestBody.image_ids = imageIds;

      const response = await fetch(DAILY_REPORT_API, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess(existingReport ? '日報を更新しました！' : '日報を提出しました！');
        // 提出後、既存の日報を再取得
        setExistingReport(data.data);
        setIsEditing(false);
        // 画像をクリア
        images.forEach(img => {
          if (img.preview) URL.revokeObjectURL(img.preview);
        });
        setImages([]);
        // モーダルを閉じる
        setIsModalOpen(false);
      } else {
        setError(data.message || '日報の提出に失敗しました');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('通信エラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      lateReason: '',
      memo: ''
    });
    // dynamicFormDataをリセット
    const resetDynamicData = {};
    fieldDefinitions.forEach(field => {
      resetDynamicData[field.field_name] = '';
    });
    setDynamicFormData(resetDynamicData);
    // プレビューURLを解放
    images.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setImages([]);
    setError('');
    setSuccess('');
  };

  const openModal = (report = null) => {
    console.log('Opening modal with report:', report);
    console.log('Current fieldDefinitions:', fieldDefinitions);
    
    if (report) {
      // 既存の日報を編集
      setExistingReport(report);
      setIsEditing(false); // 最初は閲覧モード
      // APIレスポンスの構造に対応: daily_report_content.memo を取得
      const memoValue = report.daily_report_content?.memo || report.memo || '';
      setFormData({
        date: report.work_date || new Date().toISOString().split('T')[0],
        lateReason: report.late_reason || '',
        memo: memoValue
      });

      // dynamicFormDataに既存データを読み込む
      if (report.daily_report_content && typeof report.daily_report_content === 'object') {
        // ネストされた構造に対応: daily_report_content.daily_report_content を取得
        const actualContent = report.daily_report_content.daily_report_content || report.daily_report_content;
        console.log('Loading dynamic data from report:', actualContent);
        console.log('Field definitions available:', fieldDefinitions);
        
        // 常にactualContentをセット（フィールド定義の読み込み有無に関わらず）
        console.log('Setting dynamic data:', actualContent);
        setDynamicFormData(actualContent);
      } else {
        // 既存データがない場合は初期化
        const initialData = {};
        if (fieldDefinitions.length > 0) {
          fieldDefinitions.forEach(field => {
            initialData[field.field_name] = '';
          });
        }
        setDynamicFormData(initialData);
      }

      // 既存の添付画像を表示用に変換
      if (report.files && Array.isArray(report.files)) {
        const existingImages = report.files.map(file => ({
          file: null, // 既存ファイルなのでFileオブジェクトなし
          preview: file.url || file.file_path, // URLまたはfile_path
          status: 'uploaded',
          imageId: file.file_id || file.temp_file_id,
          isExisting: true, // 既存ファイルフラグ
          fileName: file.original_name || file.file_name
        }));
        setImages(existingImages);
      } else {
        setImages([]);
      }
    } else {
      // 新規作成
      setExistingReport(null);
      setIsEditing(true);
      handleClear();
      // dynamicFormDataをフィールド定義に基づいて初期化
      const initialData = {};
      if (fieldDefinitions.length > 0) {
        fieldDefinitions.forEach(field => {
          initialData[field.field_name] = '';
        });
      }
      console.log('Creating new report with initial dynamic data:', initialData);
      setDynamicFormData(initialData);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError('');
    setSuccess('');
    // 画像をクリア
    images.forEach(img => {
      if (img.preview && !img.isExisting) {
        URL.revokeObjectURL(img.preview);
      }
    });
    setImages([]);
  };

  return (
    <div className="max-w-6xl mx-auto pt-6 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">日報管理</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/report-goals')}
            className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            目標設定
          </button>
          <button
            onClick={() => openModal()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            + 新規日報作成
          </button>
        </div>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* 日報一覧 */}
      {listLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">読み込み中...</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">日報がまだ提出されていません</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日付</th>
                
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reports.map((report) => (
                <tr key={report.report_id} className="hover:bg-gray-50">
                  <td className="px-2 md:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div>{report.work_date}</div>
                    <div className="text-xs text-gray-500">{report.user_name || '-'}</div>
                  </td>
                  <td className="px-2 md:px-6 py-4 whitespace-nowrap">
                    {report.is_late ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {report.approval_status === 'pending' ? '承認待ち' : '遅延'}
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        承認済み
                      </span>
                    )}
                  </td>
                  <td className="px-2 md:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {report.approval_status === 'approved' ? (
                      <button
                        onClick={() => openModal(report)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        詳細
                      </button>
                    ) : (
                      <button
                        onClick={() => openModal(report)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        編集
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 md:px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {existingReport && !isEditing ? '日報詳細' : existingReport ? '日報編集' : '新規日報作成'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-4 md:p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {existingReport && !isEditing && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-blue-600">この日の日報は既に提出済みです</p>
                    {!isApproved && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        編集する
                      </button>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="w-full min-w-0">
                  <FormInput
                    label="日付"
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    disabled={isReadOnly}
                  />
                </div>

                {/* 動的フィールド（フィールド定義に基づいて描画） */}
                {(() => {
                  const hasDynamicData = Object.keys(dynamicFormData).length > 0;
                  const hasFieldDefinitions = fieldDefinitions && fieldDefinitions.length > 0;
                  
                  console.log('Modal render check - hasDynamicData:', hasDynamicData, 'hasFieldDefinitions:', hasFieldDefinitions, 'dynamicFormData:', dynamicFormData);
                  
                  if (!hasDynamicData && !hasFieldDefinitions) {
                    return (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md text-center text-gray-600 text-sm">
                        カスタムフィールドが設定されていません
                      </div>
                    );
                  }
                  
                  // フィールド定義がある場合はそれを使用、ない場合はdynamicFormDataのキーを使用
                  const fieldsToRender = hasFieldDefinitions ? fieldDefinitions : Object.keys(dynamicFormData).map(key => ({
                    field_id: key,
                    field_name: key,
                    field_type: 'text', // デフォルト
                    is_required: false,
                    field_order: 0
                  }));
                  
                  return (
                    <div className="space-y-4 border-t pt-4">
                      <h3 className="text-lg font-semibold text-gray-800">カスタムフィールド</h3>
                      {fieldsToRender
                        .sort((a, b) => (a.field_order || 0) - (b.field_order || 0))
                        .map((field) => {
                          const isRequired = field.is_required === true || field.is_required === '1' || field.is_required === 1;
                          const fieldValue = dynamicFormData[field.field_name] !== undefined ? dynamicFormData[field.field_name] : '';

                          console.log(`Rendering field ${field.field_name}:`, fieldValue);

                          return (
                          <div key={field.field_id}>
                            {field.field_type === 'number' && (
                              <FormInput
                                label={field.field_name}
                                id={field.field_id}
                                name={field.field_name}
                                type="number"
                                value={fieldValue}
                                onChange={(e) => {
                                  setDynamicFormData(prev => ({
                                    ...prev,
                                    [field.field_name]: e.target.value
                                  }));
                                }}
                                placeholder={field.placeholder || '数値を入力'}
                                required={isRequired}
                                disabled={isReadOnly}
                              />
                            )}

                            {field.field_type === 'text' && (
                              <FormInput
                                label={field.field_name}
                                id={field.field_id}
                                name={field.field_name}
                                type="text"
                                value={fieldValue}
                                onChange={(e) => {
                                  setDynamicFormData(prev => ({
                                    ...prev,
                                    [field.field_name]: e.target.value
                                  }));
                                }}
                                placeholder={field.placeholder || 'テキストを入力'}
                                required={isRequired}
                                disabled={isReadOnly}
                              />
                            )}

                            {field.field_type === 'textarea' && (
                              <FormTextarea
                                label={field.field_name}
                                id={field.field_id}
                                name={field.field_name}
                                value={fieldValue}
                                onChange={(e) => {
                                  setDynamicFormData(prev => ({
                                    ...prev,
                                    [field.field_name]: e.target.value
                                  }));
                                }}
                                rows={3}
                                placeholder={field.placeholder || 'テキストを入力'}
                                required={isRequired}
                                disabled={isReadOnly}
                              />
                            )}

                            {field.field_type === 'date' && (
                              <div>
                                <label htmlFor={field.field_id} className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.field_name}
                                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <input
                                  type="date"
                                  id={field.field_id}
                                  name={field.field_name}
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setDynamicFormData(prev => ({
                                      ...prev,
                                      [field.field_name]: e.target.value
                                    }));
                                  }}
                                  required={isRequired}
                                  disabled={isReadOnly}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                              </div>
                            )}

                            {field.field_type === 'time' && (
                              <div>
                                <label htmlFor={field.field_id} className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.field_name}
                                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <input
                                  type="time"
                                  id={field.field_id}
                                  name={field.field_name}
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setDynamicFormData(prev => ({
                                      ...prev,
                                      [field.field_name]: e.target.value
                                    }));
                                  }}
                                  required={isRequired}
                                  disabled={isReadOnly}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                />
                              </div>
                            )}

                            {field.field_type === 'select' && (
                              <div>
                                <label htmlFor={field.field_id} className="block text-sm font-medium text-gray-700 mb-2">
                                  {field.field_name}
                                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                <select
                                  id={field.field_id}
                                  name={field.field_name}
                                  value={fieldValue}
                                  onChange={(e) => {
                                    setDynamicFormData(prev => ({
                                      ...prev,
                                      [field.field_name]: e.target.value
                                    }));
                                  }}
                                  required={isRequired}
                                  disabled={isReadOnly}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                >
                                  <option value="">{field.placeholder || 'オプションを選択'}</option>
                                  {field.options && (
                                    (() => {
                                      let optionsArray = [];
                                      try {
                                        // field.options は JSON 配列またはカンマ区切りの文字列
                                        if (typeof field.options === 'string') {
                                          try {
                                            // JSON パースを試みる
                                            optionsArray = JSON.parse(field.options);
                                          } catch (parseError) {
                                            // JSON パースに失敗した場合、カンマで分割
                                            optionsArray = field.options.split(',').map(opt => opt.trim()).filter(opt => opt !== '');
                                          }
                                        } else if (Array.isArray(field.options)) {
                                          optionsArray = field.options;
                                        }
                                      } catch (e) {
                                        console.error('Error parsing options:', field.options, e);
                                      }
                                      return optionsArray.map((option, idx) => (
                                        <option key={idx} value={option}>
                                          {option}
                                        </option>
                                      ));
                                    })()
                                  )}
                                </select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <FormTextarea
                  label="メモ"
                  id="memo"
                  name="memo"
                  value={formData.memo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="その他のメモや共有事項があれば記載してください"
                  disabled={isReadOnly}
                />

                {/* 画像アップロード */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    画像アップロード (最大50枚)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    disabled={isReadOnly || isUploading}
                    className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              disabled:opacity-50 disabled:cursor-not-allowed"
                  />

                  {/* アップロード進行状況 */}
                  {isUploading && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">画像アップロード中...</span>
                        <span className="text-sm font-medium text-blue-600">{Math.round(uploadProgress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* 画像プレビュー */}
                  {images.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      {images.map((imageObj, index) => (
                        <div key={index} className="relative">
                          <img
                            src={imageObj.preview}
                            alt={`プレビュー ${index + 1}`}
                            className="w-full h-32 object-cover rounded-md border border-gray-300"
                          />

                          {/* ステータスバッジ */}
                          {imageObj.status === 'uploaded' && (
                            <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                              ✓ アップロード済み
                            </div>
                          )}
                          {imageObj.status === 'error' && (
                            <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                              ✕ エラー
                            </div>
                          )}
                          {imageObj.status === 'pending' && (
                            <div className="absolute top-2 left-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">
                              待機中
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            disabled={isReadOnly || isUploading}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 disabled:opacity-50"
                          >
                            ×
                          </button>
                          <p className="mt-1 text-xs text-gray-500 truncate">
                            {imageObj.isExisting ? imageObj.fileName : imageObj.file?.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isLate && (
                  <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4">
                    <FormTextarea
                      label="遅延理由"
                      id="lateReason"
                      name="lateReason"
                      value={formData.lateReason}
                      onChange={handleChange}
                      rows={2}
                      placeholder="提出期限（当日23時）を過ぎているため、遅延理由を記載してください"
                      required
                      disabled={isReadOnly}
                    />
                  </div>
                )}

                {(!existingReport || (isEditing && !isApproved)) && (
                  <FormButtons onClear={handleClear} submitText={loading ? '提出中...' : '提出する'} disabled={loading} />
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Report;
